import React from 'react';
import { FolderOpen, Filter, Layers } from 'lucide-react';
import { Condition } from 'types/segments/segments.types';
import { useSelector } from 'react-redux';
import { selectSegments } from 'store/segments';
import Badge from 'components/ui/Badge';

interface ConditionDisplayProps {
  conditions: { [key: string]: Condition[] };
}

const ConditionDisplay: React.FC<ConditionDisplayProps> = ({ conditions }) => {
  const segments = useSelector(selectSegments);

  // Helper function to check if a condition is a group
  const isGroupCondition = (condition: Condition): boolean => {
    return condition.condition === 'group';
  };

  // Helper function to check if a condition is a segment condition
  const isSegmentCondition = (condition: Condition): boolean => {
    // Check if the field value is a segment ID (exists in segments array)
    return segments.some((segment) => segment._id === condition.field);
  };

  // Helper function to get segment name by ID
  const getSegmentName = (segmentId: string): string => {
    const segment = segments.find((seg) => seg._id === segmentId);
    return segment ? segment.name : segmentId; // Fallback to ID if segment not found
  };

  // Helper function to get operator label
  const getOperatorLabel = (operator: string): string => {
    const operatorLabels: { [key: string]: string } = {
      in_segment: 'is in segment',
      not_in_segment: 'is not in segment',
      equals: 'equals',
      not_equals: 'does not equal',
      contains: 'contains',
      not_contains: 'does not contain',
      starts_with: 'starts with',
      ends_with: 'ends with',
      greater_than: 'is greater than',
      less_than: 'is less than',
      greater_than_or_equal: 'is greater than or equal to',
      less_than_or_equal: 'is less than or equal to',
      between: 'is between',
      is_timestamp: 'is timestamp',
      is_not_timestamp: 'is not timestamp',
      is_before: 'is before',
      is_after: 'is after',
      is_between: 'is between',
      exists: 'exists',
      not_exists: 'does not exist',
      is_empty: 'is empty',
      is_not_empty: 'is not empty',
      has_property: 'has the property',
      at_least_one: 'at least one',
    };

    return operatorLabels[operator] || operator;
  };

  // Check if value is a nested condition object (has_property / at_least_one structure)
  const isNestedConditionValue = (
    val: unknown
  ): val is {
    operator: string;
    value?: unknown;
    property?: string;
    arrayType?: string;
  } => {
    return (
      typeof val === 'object' &&
      val !== null &&
      'operator' in (val as object) &&
      typeof (val as { operator: unknown }).operator === 'string'
    );
  };

  // Build path (e.g. MultiDimension[*][*]) and get leaf operator/value for nested at_least_one / has_property
  const buildPathAndLeaf = (
    rootField: string,
    conditionValue: unknown
  ): { path: string; operator: string; value: unknown } | null => {
    if (!isNestedConditionValue(conditionValue)) return null;
    const root = (rootField || '').replace(/^attributes\./, '');
    let path = root;
    let node: unknown = conditionValue;

    while (node != null && isNestedConditionValue(node)) {
      const op = node.operator;
      const innerVal = node.value;
      const prop = node.property;
      const arrayType = node.arrayType;

      if (op === 'at_least_one') {
        // Property first (e.g. .languages), then [*] for the array
        if (prop) {
          path += `.${prop}`;
        }
        if (arrayType === 'arrayValue') {
          path += '[*]';
        }
      } else if (op === 'has_property' && prop) {
        path += `.${prop}`;
      }

      if (innerVal != null && isNestedConditionValue(innerVal)) {
        node = innerVal;
      } else {
        // Leaf: current node has operator + value (possibly primitive)
        // If leaf has property (e.g. has_property value shape), append to path
        const leafPath = prop ? `${path}.${prop}` : path;
        return {
          path: leafPath,
          operator: op,
          value: innerVal,
        };
      }
    }

    // Node is leaf condition object
    if (node != null && isNestedConditionValue(node)) {
      const prop = node.property;
      const leafPath = prop ? `${path}.${prop}` : path;
      return {
        path: leafPath,
        operator: node.operator,
        value: node.value,
      };
    }
    return null;
  };

  const formatNestedConditionAsPath = (
    rootField: string,
    conditionValue: unknown
  ): string | null => {
    const result = buildPathAndLeaf(rootField, conditionValue);
    if (!result) return null;
    const { path, operator, value } = result;
    const valuePart =
      operator === 'exists' ||
      operator === 'not_exists' ||
      operator === 'is_empty' ||
      operator === 'is_not_empty'
        ? ''
        : value != null
          ? ` ${formatValue(value)}`
          : '';
    return `${path} ${getOperatorLabel(operator)}${valuePart}`.trim();
  };

  // Generate unique ID for groups with level tracking
  const generateGroupId = (
    index: number,
    level: number = 0,
    parentPath: string = ''
  ) => {
    return parentPath
      ? `${parentPath}-group-${index}`
      : `group-${level}-${index}`;
  };

  // Format the value display
  const formatValue = (value: any): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      if (value.start && value.end) {
        const formatter = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: value.timezone || 'UTC',
          second: '2-digit',
        });
        return `${formatter.format(new Date(value.start))} to ${formatter.format(new Date(value.end))} ${value.timezone ?? ''}`;
      }
      if (
        value.dateType === 'relative_date' &&
        value.days != null &&
        value.days !== ''
      ) {
        const days = value.days;
        const dir = value.timeDirection;
        const directionLabel =
          dir === 'from_now' ? 'from now' : dir === 'ago' ? 'ago' : dir || '';
        return directionLabel
          ? `${days} days ${directionLabel}`
          : `${days} days`;
      }
      if (value.dateType === 'specific_value') {
        const date = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: value.timezone || 'UTC',
          second: '2-digit',
        }).format(
          value.datetime ? new Date(value.datetime) : new Date(value.date)
        );
        return date + ' ' + (value.timezone ?? '');
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Render a single condition with support for nested groups
  const renderCondition = (
    condition: Condition,
    index: number,
    level: number = 0,
    parentPath: string = '',
    isInGroup: boolean = false
  ) => {
    const { field, operator, value } = condition;

    // If this is a group condition, render it differently
    if (isGroupCondition(condition)) {
      const groupId = generateGroupId(index, level, parentPath);
      const groupType = (value as any)?.groupType || 'and';
      const groupConditions = (value as any)?.conditions || [];

      return (
        <div
          key={groupId}
          className={`rounded-xl border overflow-hidden shadow-sm transition-shadow ${level === 0 ? 'border-primary/40 bg-card' : 'border-border bg-muted/30'} ${isInGroup ? 'ml-5 border-l-4 border-l-primary/50' : ''}`}
        >
          {/* Group Header */}
          <div
            className={`flex items-center gap-3 px-4 py-3 border-b ${level === 0 ? 'bg-primary/5 border-border' : 'bg-muted/50 border-border'}`}
          >
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-lg ${level === 0 ? 'bg-primary/15 text-primary' : 'bg-primary/10 text-primary'}`}
            >
              <FolderOpen className="w-4 h-4 shrink-0" />
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Badge
                variant={level === 0 ? 'active' : 'info'}
                size="small"
                className="shrink-0"
              >
                {groupType === 'and' ? 'All' : 'Any'}
              </Badge>
              <span className="text-sm font-medium text-foreground">
                of the following conditions match
              </span>
              {level > 0 && (
                <Badge variant="default" size="small" className="shrink-0">
                  <Layers className="w-3 h-3 mr-1" />
                  Nested
                </Badge>
              )}
            </div>
          </div>

          {/* Group Content */}
          <div className="p-4 space-y-3">
            {groupConditions.map(
              (groupCondition: Condition, conditionIndex: number) => (
                <React.Fragment key={`${groupId}-condition-${conditionIndex}`}>
                  {renderCondition(
                    groupCondition,
                    conditionIndex,
                    level + 1,
                    groupId,
                    true
                  )}
                </React.Fragment>
              )
            )}
            {groupConditions.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-6 rounded-lg bg-muted/20 border border-dashed border-border">
                No conditions in this group
              </div>
            )}
          </div>
        </div>
      );
    }

    // Check if this is a segment condition
    const isSegment = isSegmentCondition(condition);

    const isNestedOperator =
      operator === 'has_property' || operator === 'at_least_one';
    const pathDisplay =
      isNestedOperator && isNestedConditionValue(value)
        ? formatNestedConditionAsPath(field ?? '', value)
        : null;
    const displayValue =
      pathDisplay != null ? pathDisplay : formatValue(value ?? '');

    // Regular condition rendering
    return (
      <div
        key={`${parentPath}-condition-${index}`}
        className={`flex items-center gap-3 p-3 rounded-lg border bg-card border-border transition-colors hover:bg-muted/20 ${isInGroup ? 'ml-0 border-l-4 border-l-primary/40' : ''}`}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted/80 shrink-0">
          <Filter className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex items-center gap-2 flex-wrap min-w-0 text-sm">
          {isSegment ? (
            <>
              <span className="font-medium text-foreground">
                {getSegmentName(field || '')}
              </span>
              <Badge variant="default" size="small" className="font-normal">
                {getOperatorLabel(operator)}
              </Badge>
            </>
          ) : isNestedOperator && pathDisplay != null ? (
            <span className="text-foreground">{displayValue}</span>
          ) : (
            <>
              <span className="font-medium text-foreground truncate max-w-[200px]">
                {field}
              </span>
              <Badge
                variant="info"
                size="small"
                className="font-normal shrink-0"
              >
                {getOperatorLabel(operator)}
              </Badge>
              <span className="text-foreground font-medium break-all">
                {displayValue}
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  // Get the main condition type (and/or)
  const mainConditionType = Object.keys(conditions)[0] || 'and';
  const conditionList = conditions[mainConditionType] || [];

  if (conditionList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 rounded-xl border border-dashed border-border bg-muted/20">
        <Filter className="w-10 h-10 text-muted-foreground mb-3 opacity-60" />
        <p className="text-sm font-medium text-foreground">No conditions set</p>
        <p className="text-sm text-muted-foreground mt-1">
          This segment has no conditions configured.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge
          variant={mainConditionType === 'and' ? 'active' : 'info'}
          size="medium"
        >
          {mainConditionType === 'and' ? 'All' : 'Any'}
        </Badge>
        <span className="text-sm font-medium text-foreground">
          of the following conditions match
        </span>
      </div>

      <div className="space-y-3">
        {conditionList.map((condition, index) => (
          <React.Fragment key={`root-condition-${index}`}>
            {renderCondition(condition, index, 0)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ConditionDisplay;
